import type { GPTModelItem } from "@/api/models"

// Selector classification helpers for the model picker. Kept out of the
// component so they're unit-testable and so grouping logic doesn't depend on
// human-facing display text (which can be worded arbitrarily).

// "current" (GPT-5.6 + Auto) vs "legacy". Trust the backend `tier`; fall back to
// a version parse of the machine `id` (and the Auto alias) for configs served
// before the field shipped. Never keys off `display_name`.
export function isCurrentModel(model: GPTModelItem): boolean {
  if (model.tier) return model.tier === "current"
  if (model.id === "auto") return true
  const match = /gpt-(\d+(?:\.\d+)?)/i.exec(model.id)
  return match ? parseFloat(match[1]) >= 5.6 : false
}

/** Known GPT-5.6 family nicknames used for short picker subtitles. */
export type ModelFamilyHint = "auto" | "luna" | "terra" | "sol"

/**
 * Map a model id to a short family hint for UI copy. Returns null when we have
 * no curated one-liner (legacy / unknown) — the row then shows the name only.
 */
export function modelFamilyHint(modelId: string): ModelFamilyHint | null {
  const id = modelId.toLowerCase()
  if (id === "auto") return "auto"
  if (id.includes("luna")) return "luna"
  if (id.includes("terra")) return "terra"
  if (id.includes("sol")) return "sol"
  return null
}

// Picker order: Auto, then light → heavy (Luna → Terra → Sol). Unknown current
// models keep relative order after the known family.
const CURRENT_MODEL_ORDER: Record<ModelFamilyHint, number> = {
  auto: 0,
  luna: 1,
  terra: 2,
  sol: 3,
}

/** A deployment variant served from the global (non-regional) data zone. */
export function isGlobalVariant(modelId: string): boolean {
  return /global/i.test(modelId)
}

/**
 * Sort current-tier models for the picker: Auto → Luna → Terra → Sol. Within a
 * family (e.g. "Luna" and "Luna (Global)") the regional deployment comes first,
 * then the id decides, so the order never depends on catalog order — the
 * Alt+digit shortcuts are assigned from this same order.
 */
export function sortCurrentModels(models: GPTModelItem[]): GPTModelItem[] {
  return [...models]
    .map((model, index) => ({ model, index }))
    .sort((a, b) => {
      const hintA = modelFamilyHint(a.model.id)
      const hintB = modelFamilyHint(b.model.id)
      const rankA = hintA ? CURRENT_MODEL_ORDER[hintA] : 100
      const rankB = hintB ? CURRENT_MODEL_ORDER[hintB] : 100
      if (rankA !== rankB) return rankA - rankB
      if (hintA === null) return a.index - b.index
      const globalA = isGlobalVariant(a.model.id) ? 1 : 0
      const globalB = isGlobalVariant(b.model.id) ? 1 : 0
      if (globalA !== globalB) return globalA - globalB
      if (a.model.id !== b.model.id) return a.model.id < b.model.id ? -1 : 1
      return a.index - b.index
    })
    .map(({ model }) => model)
}

// Family buckets within the Legacy submenu, so a long flat list reads as
// organized sections (GPT-5 → GPT-4 → o-series → anything else).
export type LegacyGroup = {
  key: string
  labelKey: string
  labelDefault: string
  models: GPTModelItem[]
}

export function groupLegacyModels(models: GPTModelItem[]): LegacyGroup[] {
  const buckets: Record<string, GPTModelItem[]> = { gpt5: [], gpt4: [], oseries: [], other: [] }
  for (const model of models) {
    // Classify off the machine id only — display names are free-form and could
    // mention another family, skewing the bucket.
    const id = model.id.toLowerCase()
    if (/gpt-5/.test(id)) buckets.gpt5.push(model)
    else if (/gpt-4/.test(id)) buckets.gpt4.push(model)
    else if (/\bo[1-9]/.test(id)) buckets.oseries.push(model)
    else buckets.other.push(model)
  }
  return (
    [
      {
        key: "gpt5",
        labelKey: "reasoning_mode.legacy_gpt5",
        labelDefault: "GPT-5 series",
        models: buckets.gpt5,
      },
      {
        key: "gpt4",
        labelKey: "reasoning_mode.legacy_gpt4",
        labelDefault: "GPT-4 series",
        models: buckets.gpt4,
      },
      {
        key: "oseries",
        labelKey: "reasoning_mode.legacy_oseries",
        labelDefault: "Reasoning (o-series)",
        models: buckets.oseries,
      },
      {
        key: "other",
        labelKey: "reasoning_mode.legacy_other",
        labelDefault: "Other",
        models: buckets.other,
      },
    ] satisfies LegacyGroup[]
  ).filter((group) => group.models.length > 0)
}
